interface CacheItem<T> {
  data: T
  expiry: number
  version?: string
}

export class MicroCmsCache {
  private memoryCache = new Map<string, CacheItem<unknown>>()
  private maxSize = parseInt(process.env.CACHE_MAX_SIZE || '1000')
  private maxMemoryMB = parseInt(process.env.CACHE_MAX_MEMORY_MB || '100')
  private localStoragePrefix = 'microcms_cache_'

  // キャッシュ取得（メモリ → LocalStorage の順で確認）
  get<T>(key: string): T | null {
    // メモリキャッシュから確認
    let item = this.memoryCache.get(key)

    // メモリになければLocalStorageから確認
    if (!item && typeof window !== 'undefined') {
      try {
        const stored = localStorage.getItem(this.localStoragePrefix + key)
        if (stored) {
          item = JSON.parse(stored) as CacheItem<unknown>
          // メモリキャッシュにも復元
          this.memoryCache.set(key, item)
        }
      } catch (error) {
        console.warn(`Failed to parse cache from localStorage for key: ${key}`, error)
      }
    }

    // キャッシュアイテムが存在しない場合
    if (!item) {
      return null
    }

    // 期限切れチェック
    if (Date.now() > item.expiry) {
      this.memoryCache.delete(key)
      if (typeof window !== 'undefined') {
        localStorage.removeItem(this.localStoragePrefix + key)
      }
      return null
    }

    return item.data as T
  }

  // キャッシュ設定（メモリ + LocalStorage）
  set<T>(key: string, data: T, ttlMs: number): void {
    // サイズ制限チェック
    if (this.memoryCache.size >= this.maxSize) {
      this.evictOldest()
    }

    // メモリ使用量チェック（概算）
    const estimatedSize = JSON.stringify(data).length * 2 // Unicode文字を考慮
    const estimatedSizeMB = estimatedSize / (1024 * 1024)

    if (estimatedSizeMB > this.maxMemoryMB) {
      console.warn(`Cache data too large (${estimatedSizeMB.toFixed(2)}MB), skipping cache for key: ${key}`)
      return
    }

    const cacheItem: CacheItem<T> = {
      data,
      expiry: Date.now() + ttlMs,
      version: Date.now().toString()
    }

    // メモリキャッシュに保存
    this.memoryCache.set(key, cacheItem)

    // LocalStorageにも保存（ブラウザ環境のみ）
    if (typeof window !== 'undefined') {
      try {
        localStorage.setItem(this.localStoragePrefix + key, JSON.stringify(cacheItem))
      } catch (error) {
        // 容量不足の場合、古いキャッシュを削除して再試行
        if (error.name === 'QuotaExceededError') {
          console.warn('LocalStorage quota exceeded, clearing old cache entries')
          this.clearOldestLocalStorageEntries()
          try {
            localStorage.setItem(this.localStoragePrefix + key, JSON.stringify(cacheItem))
          } catch (retryError) {
            console.warn(`Failed to save cache to localStorage after cleanup for key: ${key}`, retryError)
          }
        } else {
          console.warn(`Failed to save cache to localStorage for key: ${key}`, error)
        }
      }
    }
  }

  // 最も古いキャッシュエントリを削除
  private evictOldest(): void {
    const oldestKey = this.memoryCache.keys().next().value
    if (oldestKey) {
      this.memoryCache.delete(oldestKey)
      console.log(`Cache evicted oldest entry: ${oldestKey}`)
    }
  }

  // LocalStorageの古いエントリを削除（容量不足対策）
  private clearOldestLocalStorageEntries(): void {
    if (typeof window === 'undefined') return

    try {
      const keys = Object.keys(localStorage)
        .filter(key => key.startsWith(this.localStoragePrefix))
        .map(key => {
          try {
            const item = JSON.parse(localStorage.getItem(key) || '{}')
            return { key, expiry: item.expiry || 0 }
          } catch {
            return { key, expiry: 0 }
          }
        })
        .sort((a, b) => a.expiry - b.expiry) // 期限切れが近い順

      // 古い順に3つ削除
      keys.slice(0, 3).forEach(({ key }) => {
        localStorage.removeItem(key)
        console.log(`Removed old localStorage entry: ${key}`)
      })
    } catch (error) {
      console.warn('Failed to clear old localStorage entries:', error)
    }
  }

  // キャッシュクリア
  clear(key?: string): void {
    if (key) {
      this.memoryCache.delete(key)
      if (typeof window !== 'undefined') {
        localStorage.removeItem(this.localStoragePrefix + key)
      }
    } else {
      this.memoryCache.clear()
      // LocalStorageからも全削除
      if (typeof window !== 'undefined') {
        const keys = Object.keys(localStorage)
        keys.forEach(k => {
          if (k.startsWith(this.localStoragePrefix)) {
            localStorage.removeItem(k)
          }
        })
      }
    }
  }

  // パターンマッチングでキャッシュクリア
  clearPattern(pattern: string): void {
    const regex = new RegExp(pattern.replace('*', '.*'))

    // メモリキャッシュクリア
    const keys = Array.from(this.memoryCache.keys())
    for (const key of keys) {
      if (regex.test(key)) {
        this.memoryCache.delete(key)
      }
    }

    // LocalStorageからもクリア
    if (typeof window !== 'undefined') {
      const storageKeys = Object.keys(localStorage)
      for (const storageKey of storageKeys) {
        if (storageKey.startsWith(this.localStoragePrefix)) {
          const cacheKey = storageKey.replace(this.localStoragePrefix, '')
          if (regex.test(cacheKey)) {
            localStorage.removeItem(storageKey)
          }
        }
      }
    }
  }

  // Stale-While-Revalidate パターン
  async getWithSWR<T>(key: string, fetcher: () => Promise<T>, ttlMs: number): Promise<T> {
    const cached = this.get<T>(key)

    if (cached) {
      // バックグラウンドで更新
      setTimeout(async () => {
        try {
          const fresh = await fetcher()
          this.set(key, fresh, ttlMs)
        } catch (error) {
          console.warn(`Background refresh failed for ${key}:`, error)
        }
      }, 0)

      return cached
    }

    // 初回取得
    const fresh = await fetcher()
    this.set(key, fresh, ttlMs)
    return fresh
  }

  // キャッシュされた時刻を取得
  getCachedTime(key: string): number | null {
    let item = this.memoryCache.get(key)

    // メモリになければLocalStorageから確認
    if (!item && typeof window !== 'undefined') {
      try {
        const stored = localStorage.getItem(this.localStoragePrefix + key)
        if (stored) {
          item = JSON.parse(stored) as CacheItem<unknown>
          // メモリキャッシュにも復元
          this.memoryCache.set(key, item)
        }
      } catch (error) {
        console.warn(`Failed to parse cache from localStorage for key: ${key}`, error)
      }
    }

    // キャッシュアイテムが存在しない場合
    if (!item) {
      return null
    }

    // 期限切れチェック
    if (Date.now() > item.expiry) {
      this.memoryCache.delete(key)
      if (typeof window !== 'undefined') {
        localStorage.removeItem(this.localStoragePrefix + key)
      }
      return null
    }

    return item.version ? parseInt(item.version) : null
  }

  // デバッグ用: キャッシュ状態確認
  getStats(): { total: number; keys: string[] } {
    return {
      total: this.memoryCache.size,
      keys: Array.from(this.memoryCache.keys())
    }
  }
}
